import type { ParseError } from '../result.ts';
import type { ImageRef, StoryboardSpec, SubtitleTrackRef } from './media.ts';
import type { RichNode } from './richText.ts';

/**
 * The abbreviated video record: `/api/v1/search`, `recommendedVideos[]`, `/api/v1/popular`.
 *
 * §2 trap 6: `type: "shortVideo"` is **not** a YouTube Short — it is Invidious's `ChannelVideo`
 * serialization, and `/popular` returns 30-minute videos under it. There is no Shorts
 * discriminator in the API, so this type covers both.
 *
 * R7: no formatted strings. `viewCountText` / `publishedText` / `subCountText` are dropped on the
 * floor — they are server-locale-bound (this instance answers in Arabic) and inconsistent across
 * instances. The cost is real and worth naming: `recommendedVideos[]` carries *only*
 * `viewCountText`, so `viewCount` is `null` for recommendations until they are hydrated.
 */
export interface VideoSummary {
  readonly videoId: string;
  readonly title: string;
  readonly author: string;
  readonly authorId: string | null;
  /** Relative (`/channel/UC…`) — an in-app route, not a URL to fetch. */
  readonly authorUrl: string | null;
  readonly authorVerified: boolean;
  readonly lengthSeconds: number;
  readonly publishedEpochSec: number | null;
  readonly viewCount: number | null;
  readonly thumbnails: readonly ImageRef[];
  readonly liveNow: boolean;
  readonly isUpcoming: boolean;
  readonly premiereAtEpochSec: number | null;
}

/**
 * What the corner of a listing thumbnail says. §2's rule — branch on the booleans, never on `type`
 * — as a decision the platform cannot get subtly wrong twice.
 */
export type VideoBadge =
  | { readonly kind: 'live' }
  | { readonly kind: 'upcoming'; readonly premiereAtEpochSec: number | null }
  | { readonly kind: 'duration'; readonly seconds: number }
  /** No duration, and nothing in the record saying why. Rendered as *nothing* — see below. */
  | { readonly kind: 'unknown' };

/**
 * `liveNow` first, then `isUpcoming`, then a duration — a stream that has started is live even
 * while the record still calls it upcoming.
 *
 * The `unknown` arm is the one that has to exist. `recommendedVideos[]` carries **no `liveNow` and
 * no `isUpcoming` at all** (measured: eight live videos, 96 records, neither key present), and a
 * live entry among them reports `lengthSeconds: 0` — 3 of those 96, e.g. "LIVE: NBC News NOW" in
 * the committed `live-hls-Pz4W67_002M` fixture. So the honest reading of a zero duration in a
 * listing is "this row does not know how long it is", and the platform draws no badge. Inferring
 * `live` from it would be inventing a fact the payload does not carry — and inferring `0:00`, which
 * is what a duration formatter does with it, states one that is false.
 */
export function videoBadgeOf(video: {
  readonly liveNow: boolean;
  readonly isUpcoming: boolean;
  readonly lengthSeconds: number;
  readonly premiereAtEpochSec: number | null;
}): VideoBadge {
  if (video.liveNow) return { kind: 'live' };
  if (video.isUpcoming) return { kind: 'upcoming', premiereAtEpochSec: video.premiereAtEpochSec };
  if (Number.isFinite(video.lengthSeconds) && video.lengthSeconds > 0) {
    return { kind: 'duration', seconds: video.lengthSeconds };
  }
  return { kind: 'unknown' };
}

/** `GET /api/v1/videos/{id}`. */
export interface VideoDetails {
  readonly videoId: string;
  readonly title: string;
  /**
   * Plain text, exactly as YouTube's `shortDescription` gives it — the honest copy of what the
   * author typed, and the input to both {@link parseChapters} and the node tree below.
   */
  readonly description: string;
  /**
   * R8's node tree. `descriptionHtml` is **not** carried: it is consumed inside the parser (for
   * mention targets only) and never leaves it, so there is no HTML string in the model for a
   * renderer to reach for. See `parse/richText.ts` for why the tree is built from the plain text.
   */
  readonly descriptionNodes: readonly RichNode[];
  readonly author: string;
  readonly authorId: string | null;
  readonly authorUrl: string | null;
  readonly authorVerified: boolean;
  readonly authorThumbnails: readonly ImageRef[];
  readonly publishedEpochSec: number | null;
  readonly lengthSeconds: number;
  readonly viewCount: number | null;
  readonly likeCount: number | null;
  /** §2: the field exists, the data does not. Hidden by default — kept so the UI can decide. */
  readonly dislikeCount: number | null;
  readonly rating: number | null;
  readonly genre: string | null;
  readonly keywords: readonly string[];
  readonly isFamilyFriendly: boolean;
  /** ISO-3166 codes. Empty means the API said nothing, *not* "blocked everywhere". */
  readonly allowedRegions: readonly string[];
  readonly isListed: boolean;
  readonly allowRatings: boolean;
  readonly paid: boolean;
  readonly premium: boolean;
  readonly liveNow: boolean;
  readonly isUpcoming: boolean;
  readonly isPostLiveDvr: boolean;
  readonly premiereAtEpochSec: number | null;
  readonly thumbnails: readonly ImageRef[];
  readonly storyboard: StoryboardSpec | null;
  readonly subtitles: readonly SubtitleTrackRef[];
  /** Transport-resolved, `?local=true` applied. `null` when the API omitted it. */
  readonly dashUrl: string | null;
  /** Transport-resolved, `?local=true` applied. Present on live; absent on VOD — and, per §4, absent on at least one `isPostLiveDvr` video too. */
  readonly hlsUrl: string | null;
  /** `{transport}/latest_version?id=…&itag=18&local=true` — the proxied no-MSE path. */
  readonly progressiveFallbackUrl: string | null;
  /** Read off the signed media URLs (`expire=`), ~5h45m out. Drives 403 recovery. */
  readonly mediaExpiresAtEpochSec: number | null;
  readonly recommended: readonly VideoSummary[];
  /**
   * R4 — entries of `captions[]` and `recommendedVideos[]` that dropped themselves, kept so they
   * stay *counted*. Both lists silently discarded their failures before this; a count that
   * dies inside the parser cannot tell "this video has no subtitles" from "we threw them away".
   */
  readonly dropped: readonly ParseError[];
}
